import { requestInstance } from '@/helpers/requestInstance';
import { requestInstanceV2 } from '@/helpers/requestInstanceV2';
import {
	type ImageGenModel,
	type TProjectApiArgs,
	type TUploadProjectImageArgs,
	type TUploadProjectImageResponse, VideoGenModel,
} from '@/types';
import { API_DOMAINS, ENV } from '@/constants';

export interface UserDataResponse {
	ip: string;
	city: string;
	postal_code: string;
	country_code: string;
	subdivision: string;
}

export const ApiService = {
	async getUserData(ip?: string) {
		const url = ip
			? `${API_DOMAINS.GET_USER_DATA}${ip}`
			: API_DOMAINS.GET_USER_DATA;
		return await requestInstanceV2<unknown, UserDataResponse>({
			url,
			method: 'get',
			headers: { accept: 'application/json' },
		});
	},
	async sendUserData(data: any) {
		return await requestInstance(API_DOMAINS.SEND_USER_DATA, data, 'POST');
	},
	async sendWebPixelData(data: {
		fbc: string;
		pixelId: string;
		eventType: string;
		body: any;
	}) {
		return await requestInstance(API_DOMAINS.SEND_WEB_PIXEL_DATA, data, 'PUT');
	},
	async getUserCurrencyInfo(): Promise<{ data: UserCurrencyInfo }> {
		return await requestInstance(
			API_DOMAINS.GET_USER_CURRENCY_INFO,
			null,
			'GET',
		);
	},
	async logDirectEvent(
		eventType: string,
		uuid: string,
		eventProperties?: Record<string, unknown>,
		userProperties?: Record<string, unknown>,
		userId?: string,
	) {
		if (typeof window === 'undefined') return;

		const body = JSON.stringify({
			api_key: ENV.AMPLITUDE_ID,
			events: [
				{
					device_id: uuid,
					event_type: eventType,
					event_properties: eventProperties ?? {},
					user_properties: userProperties ?? {},
					user_id: userId,
				},
			],
		});

		// keepalive so the request survives an immediate navigation (e.g. a CTA
		// that redirects right after logging) — otherwise the event is lost.
		// credentials:'omit' + Content-Type application/json matches Amplitude's
		// CORS behavior (see postToAmplitudeApi in amplitudeService).
		void fetch('https://api2.amplitude.com/2/httpapi', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
			keepalive: true,
			credentials: 'omit',
			mode: 'cors',
		}).catch(() => console.log(`Amplitude Error in ${eventType} event`));
	},

	async uploadFileToAmazon({
		uploadUrl,
		formData,
		signal,
	}: {
		uploadUrl: string;
		formData: FormData;
		signal?: AbortSignal;
	}) {
		const response = await requestInstanceV2({
			url: uploadUrl,
			method: 'post',
			data: formData,
			headers: { 'content-type': 'multipart/form-data' },
			signal,
		});

		return response.data;
	},

	async uploadProjectImage({
		mimeType,
		signal,
	}: TUploadProjectImageArgs & TProjectApiArgs) {
		const response = await requestInstanceV2<
			TUploadProjectImageArgs,
			TUploadProjectImageResponse
		>({
			url: `${API_DOMAINS.UPLOAD_IMAGE}`,
			method: 'post',
			data: {
				mimeType,
			},
			signal,
		});

		// error 400, 401, 404
		return response.data;
	},

	async getImageGenModels() {
		return await requestInstanceV2<unknown, ImageGenModel[]>({
			url: API_DOMAINS.GET_IMAGE_GEN_MODELS,
			method: 'get',
		});
	},

	async getVideoGenModels() {
		return await requestInstanceV2<unknown, VideoGenModel[]>({
			url: API_DOMAINS.GET_VIDEO_GEN_MODELS,
			method: 'get',
		});
	},
};

export interface UserCurrencyInfo {
	symbol: string;
	rate: number;
	code: string;
}
